import {
  ApiErrorStatusCode,
  getAsyncStatus,
  getAsyncStatusFromRtkQueryStates,
  isQueryError,
} from 'lib-shared-auth';
import { useCallback, useMemo, useState } from 'react';

import { customFieldsApi } from 'apis/customFields';

import { getCustomFields } from '../getCustomFields';

import {
  CustomField,
  ReadCustomFieldsFn,
  UseLazyCustomFieldsResult,
} from './useLazyCustomFields.types';

/**
 * A hook for lazily getting custom fields (attributes and their values) of an
 * instance of an entity.
 */
export const useLazyCustomFields = (): UseLazyCustomFieldsResult => {
  const [readAttributes, attributesResponse] =
    customFieldsApi.useLazyReadAttributesQuery();
  const [readValue, valueResponse] = customFieldsApi.useLazyReadValueQuery();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);

  const rawResponse = useMemo(
    () => ({
      attributes: attributesResponse,
      value: valueResponse,
    }),
    [attributesResponse, valueResponse],
  );

  const customFields = useMemo<readonly CustomField[]>(() => {
    return getCustomFields(
      attributesResponse.data?.data.attributes,
      valueResponse.data?.data.attribute_values,
      { includeArchived, includeDeactivated },
    );
  }, [
    attributesResponse.data?.data.attributes,
    includeArchived,
    includeDeactivated,
    valueResponse.data?.data.attribute_values,
  ]);

  const readAttributesAsyncStatus =
    getAsyncStatusFromRtkQueryStates(attributesResponse);
  const readValuesAsyncStatus = getAsyncStatusFromRtkQueryStates(valueResponse);

  const isReadValueNotFound =
    isQueryError(valueResponse.error) &&
    valueResponse.error.status === ApiErrorStatusCode.NotFound;

  const asyncStatus = isReadValueNotFound
    ? readAttributesAsyncStatus
    : getAsyncStatus([readAttributesAsyncStatus, readValuesAsyncStatus]);

  const trigger = useCallback<ReadCustomFieldsFn>(
    ({
      entityId,
      entityInstanceId,
      includeArchived = false,
      includeDeactivated = false,
    }) => {
      readAttributes({ entityId });
      readValue({ entityId, entityInstanceId });
      setIncludeArchived(includeArchived);
      setIncludeDeactivated(includeDeactivated);
    },
    [readAttributes, readValue],
  );

  const result = useMemo(
    () => ({
      customFields,
      asyncStatus,
      rawResponse,
    }),
    [asyncStatus, customFields, rawResponse],
  );

  return useMemo(() => [trigger, result], [result, trigger]);
};
